import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import {
    CellMeasurer,
    CellMeasurerCache,
    createMasonryCellPositioner,
    Masonry,
    AutoSizer,
    MasonryCellProps,
    Positioner
} from 'react-virtualized'
import ListItem from './ListItem'
import NoDataToDisplay from './NoDataToDisplay'
import { WizardStep } from 'models'

type MasonryComponentProps = {
    partnerId: string,
    list: any[],
    step: WizardStep,
    masonryRef: MutableRefObject<any>,
    listContainerRef: any,
    [key: string]: any
}
const INITIAL_COL_GUTTER = 8
const DEFAULT_COL_WIDTH = 200
const UINT32 = new Uint32Array(1)

const MasonryComponent = ({ partnerId, list, step, masonryRef, setForceUpdate, listContainerRef }: MasonryComponentProps) => {
    const [colWidth, setColWidth] = useState(DEFAULT_COL_WIDTH)
    const [colCount, setColCount] = useState(1)
    const colWidthRef = useRef(colWidth)
    colWidthRef.current = colWidth
    const colCountRef = useRef(colCount)
    const cache = new CellMeasurerCache({
        defaultHeight: 200,
        defaultWidth: colWidth,
        minHeight: 55,
        fixedWidth: true
    })
    const cacheRef = useRef<CellMeasurerCache>(cache)
    cacheRef.current = cache
    const cellPositioner = createMasonryCellPositioner({
        cellMeasurerCache: cache,
        columnCount: colCount,
        columnWidth: colWidth,
        spacer: INITIAL_COL_GUTTER * colCount
    })
    const cellPositionerRef = useRef<Positioner>(cellPositioner)
    cellPositionerRef.current = cellPositioner
    const _listContainerRef = useRef(listContainerRef)

    const calculateColCount = useCallback(() => {
        const col = step.gridColSize && step.gridColSize > 1 ? 2 : 1
        setColCount(col)
        colCountRef.current = col
    }, [step.gridColSize])

    const calculateColWidth = useCallback(() => {
        calculateColCount()
        let updatedGutter = INITIAL_COL_GUTTER * colCountRef.current
        if (_listContainerRef?.current?.offsetWidth) {
            colWidthRef.current = (_listContainerRef.current.offsetWidth - (updatedGutter + 7)) / colCountRef.current
            // incase the width is too small, then span the whole row
            if (colWidthRef.current < 160 || colCountRef.current === 1) {
                colWidthRef.current = _listContainerRef.current.offsetWidth - 7
                colCountRef.current = 1
                updatedGutter = INITIAL_COL_GUTTER
            }
        }
        setColWidth(colWidthRef.current)
        return updatedGutter
    }, [calculateColCount])

    const resetMasonryPositions = useCallback(() => {
        const updatedGutter = calculateColWidth()
        if (masonryRef?.current && cacheRef.current && colCountRef.current && colWidthRef.current) {
            cacheRef.current.clearAll()
            cellPositionerRef.current.reset({
                columnCount: colCountRef.current,
                columnWidth: colWidthRef.current,
                spacer: updatedGutter
            })
            masonryRef.current.clearCellPositions()
            masonryRef.current.recomputeCellPositions()
        }
    }, [calculateColWidth, masonryRef])

    useEffect(() => {
        resetMasonryPositions()
    }, [step, list, resetMasonryPositions])

    const cellRenderer = useCallback(({ index, key, parent, style }: MasonryCellProps) => {
        const datum = list[index];
        if (partnerId && cacheRef.current) {
            return <CellMeasurer cache={cacheRef.current} index={index} key={`${key}-${index}`} parent={parent}>
                <div style={style} key={key}>
                    <ListItem item={datum} partnerId={partnerId} step={step}></ListItem>
                </div>
            </CellMeasurer>
        }
        return <></>
    }, [partnerId, step, list])

    if (list.length) {
        return <>
            <AutoSizer onResize={() => {
                // force masonry re-render when screen size change
                // Note: Need to fix glitch in column count calculation on screen re-size on some points
                setForceUpdate(crypto.getRandomValues(UINT32))
            }}>
                {({ height, width }) =>
                    <Masonry
                        cellCount={list.length}
                        cellMeasurerCache={cacheRef.current}
                        cellPositioner={cellPositionerRef.current}
                        cellRenderer={cellRenderer}
                        height={height}
                        width={width}
                        ref={ref => masonryRef.current = ref}
                        keyMapper={(index) => list[index]?.id}
                        overscanByPixels={500}
                        autoHeight={false}
                    />
                }
            </AutoSizer>
        </>
    }
    return <NoDataToDisplay />
}

export default observer(MasonryComponent)